import { Link } from "react-router-dom";
import { mauCuaTram } from "../data/mauTram.js";
import { cacTram, layTram, linkViec, linkTram, SO_TRAM } from "../data/lo-trinh.js";
import { TOOLS } from "../data/tools.js";

/**
 * Tiến độ theo đúng 5 trạm của Lộ trình — Lộ trình và Bản chuẩn bị dùng chung
 * một danh sách việc, không có checklist thứ hai.
 *
 * Trạng thái một việc:
 *   - "xong"       : người dùng tự đánh dấu (data.tasks["viec:<tram>:<i>"]).
 *   - "co-ket-qua" : đã có bản nháp/kịch bản của công cụ tương ứng, hoặc đã lưu
 *                    nguồn vốn (việc lọc quỹ). Có kết quả chưa chắc là xong.
 *   - "chua"       : chưa có gì. Chỉ mở công cụ thì vẫn là "chua".
 */
export const khoaViec = (tram, i) => `viec:${tram.id}:${i}`;

const congCuCua = (viec) =>
  viec.dich?.loai === "congCu" ? TOOLS.find((t) => t.slug === viec.dich.slug) : null;

export function trangThaiViec(data, tram, viec, i) {
  if (data.tasks?.[khoaViec(tram, i)]) return "xong";
  const t = congCuCua(viec);
  if (t && (data.drafts?.[t.toolId] || (data.scenarios || []).some((s) => s.tool === t.toolId))) return "co-ket-qua";
  if (viec.dich?.loai === "congCu" && viec.dich.slug === "quy-phu-hop" && (data.saved || []).length) return "co-ket-qua";
  return "chua";
}

/** Việc tiếp theo: việc đầu tiên còn trống, bắt đầu từ trạm được gợi ý (nếu có). */
export function viecTiepTheoCuaToi(data, tramGoiY) {
  const ds = cacTram();
  const batDau = tramGoiY ? ds.findIndex((t) => t.so === tramGoiY) : -1;
  const thuTu = batDau > 0 ? [...ds.slice(batDau), ...ds.slice(0, batDau)] : ds;
  for (const tram of thuTu) {
    for (let i = 0; i < tram.viec.length; i++) {
      if (trangThaiViec(data, tram, tram.viec[i], i) !== "chua") continue;
      const truoc = i > 0 ? tram.viec[i - 1] : null;
      const truocCoKq = truoc && trangThaiViec(data, tram, truoc, i - 1) !== "chua";
      const ly = truocCoKq
        ? `Bạn đã có kết quả cho “${truoc.ten.toLowerCase()}”. Dùng kết quả đó cho việc kế tiếp.`
        : tramGoiY && tram.so === tramGoiY
          ? `Theo lần chấm Điểm sẵn sàng, bạn nên bắt đầu ở Trạm ${tram.so}.`
          : `Việc đầu tiên còn trống trên lộ trình.`;
      return { tram, viec: tram.viec[i], stt: i + 1, ly };
    }
  }
  return null;
}

const NHAN_TT = { xong: "Đã xong", "co-ket-qua": "Có kết quả", chua: "Chưa làm" };

export default function TienDoLoTrinh({ data, onDanhDau, nganh = "", anDau = false }) {
  return (
    <section className={anDau ? "" : "section-space"} aria-labelledby={anDau ? undefined : "ws-tien-do"} aria-label={anDau ? "Tiến độ các trạm" : undefined}>
      {!anDau && (<>
      <div className="section-bar">
        <h2 id="ws-tien-do">Tiến độ {SO_TRAM} trạm</h2>
        <Link to="/lo-trinh">Xem lộ trình →</Link>
      </div>
      <p className="nho muted">
        Việc có bản lưu từ công cụ hiện “Có kết quả”. Chỉ đánh dấu “Đã xong” khi kết quả đó đủ dùng cho hồ sơ.
      </p>
      </>)}
      <div className="ws-tram-bang">
        {cacTram().map((tram) => {
          const tt = tram.viec.map((v, i) => trangThaiViec(data, tram, v, i));
          const coGi = tt.filter((x) => x !== "chua").length;
          const xong = tt.every((x) => x === "xong");
          const iTiep = tt.findIndex((x) => x === "chua");
          const ketQua = [
            ...tram.viec
              .map((v) => congCuCua(v))
              .filter((t) => t && data.drafts?.[t.toolId])
              .map((t) => t.title),
            ...(tram.id === "tim-quy-va-tiep-can" && data.saved?.length ? [`${data.saved.length} quỹ mục tiêu`] : []),
          ];
          const trangThai = xong ? "Đã xong" : coGi ? `Đang làm ${coGi}/${tram.viec.length} việc` : "Chưa bắt đầu";
          return (
            <details key={tram.id} className={`ws-tram${xong ? " xong" : coGi ? " dang-lam" : ""}`} style={{ "--mau": mauCuaTram(tram) }}>
              <summary>
                {/* Vòng tiến độ đầu mỗi trạm: phần tô = số việc đã có kết quả. */}
                <span className="ws-tram-vong" aria-hidden="true">
                  <svg viewBox="0 0 40 40">
                    <circle cx="20" cy="20" r="16" className="ws-vong-nen" />
                    {coGi > 0 && (
                      <circle cx="20" cy="20" r="16" className="ws-vong-day" transform="rotate(-90 20 20)"
                        strokeDasharray={`${(coGi / tram.viec.length) * 100.5} 100.5`} />
                    )}
                  </svg>
                  <b>{tram.so}</b>
                </span>
                <span className="ws-tram-ten">{tram.ten}</span>
                <span className="ws-tram-tt">{trangThai}</span>
                <span className="ws-tram-kq">{ketQua.length ? ketQua.join(", ") : "Chưa có kết quả"}</span>
              </summary>
              <ol className="ws-tram-viec">
                {tram.viec.map((v, i) => (
                  <li key={v.ten} className={`tt-${tt[i]}`}>
                    <div>
                      <strong>{v.ten}</strong>
                      <span className="ws-viec-nhan">{NHAN_TT[tt[i]]}</span>
                    </div>
                    <div className="ws-viec-hanh-dong">
                      <Link to={linkViec(tram, v, nganh)}>{tt[i] === "chua" ? v.nhan : "Mở lại"} →</Link>
                      <label className="hop-chon">
                        <input
                          type="checkbox"
                          checked={tt[i] === "xong"}
                          onChange={(e) => onDanhDau(khoaViec(tram, i), e.target.checked)}
                        />
                        Đã xong
                      </label>
                    </div>
                  </li>
                ))}
              </ol>
              <p className="nho">
                {iTiep >= 0
                  ? <Link className="nut phu" to={linkViec(tram, tram.viec[iTiep], nganh)}>{coGi ? "Tiếp tục" : "Bắt đầu"} trạm {tram.so} →</Link>
                  : <Link to={linkTram(tram)}>Xem lại trạm {tram.so} →</Link>}
              </p>
            </details>
          );
        })}
      </div>
    </section>
  );
}

export { layTram };
